import { useEffect, useRef } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import {
  associatePublicDecksWithTeam,
  downgradeOwnTeamDecks,
  notifyTeamDecksPrivatized,
} from '../utils/teamDeckCleanup';
import { clearTeamSharedCollection } from '../utils/teamSharedCollectionSync';

/**
 * When the user loses their teamId (leave / kick / delete), privatize team decks
 * and clear any leftover shared-collection projection for the previous team.
 * When they gain a teamId, link existing public decks to the team profile.
 */
export function useTeamDeckCleanup() {
  const { user } = useAuth();
  const prevTeamId = useRef<string | null | undefined>(undefined);
  const linkedTeamId = useRef<string | null>(null);

  useEffect(() => {
    if (!user) {
      prevTeamId.current = undefined;
      linkedTeamId.current = null;
      return;
    }

    const prev = prevTeamId.current;
    const next = user.teamId;

    if (prev && !next) {
      const oldTeamId = prev;
      linkedTeamId.current = null;
      void (async () => {
        try {
          await clearTeamSharedCollection(oldTeamId, user.uid);
        } catch {
          // ignore
        }
        try {
          const count = await downgradeOwnTeamDecks(user.uid, oldTeamId);
          notifyTeamDecksPrivatized(count);
        } catch (err) {
          console.warn('[useTeamDeckCleanup] failed', err);
        }
      })();
    } else if (next && linkedTeamId.current !== next) {
      linkedTeamId.current = next;
      void associatePublicDecksWithTeam(user.uid, next).catch((err) => {
        linkedTeamId.current = null;
        console.warn('[useTeamDeckCleanup] associate public decks failed', err);
      });
    }

    prevTeamId.current = next;
  }, [user?.uid, user?.teamId]);
}
